import { Title } from '@mantine/core';
import {
  formatSearchQuery,
  getReferenceString,
  parseSearchRequest,
  SearchRequest,
} from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { Document, useMedplum, useMedplumNavigate } from '@medplum/react';
import { Outlet, useLocation } from 'react-router-dom';
import { SearchControl } from '@medplum-components/SearchControl/SearchControl';
import { useEffect, useState } from 'react';
import { addDefaultSearchValues } from '@utils/search-utils';
import { useTranslation } from 'react-i18next';

/**
 * Home page that greets the user and displays a list of patients.
 * @returns A React component that displays the home page.
 */
export function HomePage(): JSX.Element {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);

  const navigate = useMedplumNavigate();

  const location = useLocation();
  const [search, setSearch] = useState<SearchRequest>({
    resourceType: 'Patient',
  });

  useEffect(() => {
    const parsedSearch = parseSearchRequest(
      location.pathname + location.search
    );
    const populatedSearch = addDefaultSearchValues(
      {
        ...parsedSearch,
        resourceType: 'Patient',
      },
      ['name', 'birthDate', 'gender', '_lastUpdated']
    );

    if (location.search === formatSearchQuery(populatedSearch)) {
      setSearch(populatedSearch);
    } else {
      navigate(`/patients${formatSearchQuery(populatedSearch)}`);
    }
  }, [medplum, navigate, location]);

  return (
    <>
      <Document>
        <Title order={1}>
          {t(`layout.sidebar.patients`, { ns: 'common' })}
        </Title>
        <SearchControl
          search={search}
          onClick={(e) => navigate(`/${getReferenceString(e.resource)}`)}
          onChange={(e) => {
            navigate(`/patients${formatSearchQuery(e.definition)}`);
          }}
          hideToolbar
          renderCell={(resource, field) => {
            if (
              resource.resourceType === 'Patient' &&
              field.name === 'gender'
            ) {
              const code = (resource as Patient).gender;
              if (!code) {
                return undefined;
              }
              return t(`summaryPage.summary.gender.${code}`, {
                ns: 'patient-pages',
                defaultValue: code,
              });
            }
            return undefined;
          }}
        />
        <Outlet />
      </Document>
    </>
  );
}
